import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  ConsolidationGroupRecord,
  ConsolidationRunEntry,
  ConsolidationRunRecord,
  FinanceApiService
} from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-consolidation',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, CurrencySelectorComponent],
  providers: [MessageService],
  template: `
    <ain-page title="Consolidation Workspace" description="Manage groups, execute runs, and inspect generated entries">
      @if (!capabilityEnabled()) {
        <ain-state type="empty" title="Consolidation capability is disabled" message="Enable financial-core.consolidation in control center." />
      } @else {
      <section class="section">
        <h3>Create Group</h3>
        <div class="toolbar">
          <input pInputText [(ngModel)]="groupCode" placeholder="Code" />
          <input pInputText [(ngModel)]="groupName" placeholder="Name" />
          <ain-currency-selector [(ngModel)]="baseCurrencyCode" [showSearch]="true"></ain-currency-selector>
          <button pButton type="button" label="Create" (click)="createGroup()"></button>
        </div>
      </section>

      <section class="section">
        <h3>Groups</h3>
        <p-table [value]="groups()">
          <ng-template pTemplate="header"><tr><th>Code</th><th>Name</th><th>Base Currency</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td>{{ row.code }}</td><td>{{ row.name }}</td><td>{{ row.baseCurrencyCode }}</td></tr></ng-template>
        </p-table>
      </section>

      <section class="section">
        <h3>Create Run</h3>
        <div class="toolbar">
          <input pInputText [(ngModel)]="runGroupId" placeholder="Group id" />
          <input pInputText [(ngModel)]="fiscalYearText" placeholder="Fiscal year" />
          <input pInputText [(ngModel)]="periodNumberText" placeholder="Period number" />
          <input pInputText [(ngModel)]="eliminationAmountText" placeholder="Elimination amount" />
          <button pButton type="button" label="Create" (click)="createRun()"></button>
        </div>
      </section>

      <section class="section">
        <h3>Runs</h3>
        <p-table [value]="runs()">
          <ng-template pTemplate="header"><tr><th>Id</th><th>Group</th><th>FiscalYear</th><th>Period</th><th>Status</th><th>Entries</th></tr></ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.id }}</td>
              <td>{{ row.groupId }}</td>
              <td>{{ row.fiscalYear }}</td>
              <td>{{ row.periodNumber }}</td>
              <td>{{ row.status }}</td>
              <td><button pButton type="button" size="small" label="Load" (click)="loadEntries(row.id)"></button></td>
            </tr>
          </ng-template>
        </p-table>
      </section>

      <section class="section">
        <h3>Entries</h3>
        <p-table [value]="entries()">
          <ng-template pTemplate="header"><tr><th>Type</th><th>Description</th><th>Amount</th><th>Currency</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td>{{ row.entryType }}</td><td>{{ row.description }}</td><td>{{ row.amount }}</td><td>{{ row.currencyCode }}</td></tr></ng-template>
        </p-table>
      </section>
      }
    </ain-page>
  `,
  styles: [`
    .section { display: grid; gap: 10px; margin-bottom: 24px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
  `]
})
export class FinanceConsolidationComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly messages = inject(MessageService);

  readonly groups = signal<ConsolidationGroupRecord[]>([]);
  readonly runs = signal<ConsolidationRunRecord[]>([]);
  readonly entries = signal<ConsolidationRunEntry[]>([]);
  readonly capabilityEnabled = signal(false);

  groupCode = '';
  groupName = '';
  baseCurrencyCode = 'IRR';
  runGroupId = '';
  fiscalYearText = `${new Date().getUTCFullYear()}`;
  periodNumberText = '1';
  eliminationAmountText = '0';

  ngOnInit(): void {
    this.api.getCapabilitySetting('financial-core.consolidation').subscribe({
      next: setting => {
        this.capabilityEnabled.set(setting.isEnabled);
        if (setting.isEnabled) this.loadAll();
      }
    });
  }

  loadAll(): void {
    this.api.listConsolidationGroups().subscribe({ next: rows => this.groups.set(rows) });
    this.api.listConsolidationRuns().subscribe({ next: rows => this.runs.set(rows) });
  }

  createGroup(): void {
    if (!this.groupCode.trim() || !this.groupName.trim() || !this.baseCurrencyCode.trim()) return;
    this.api.createConsolidationGroup({
      code: this.groupCode.trim(),
      name: this.groupName.trim(),
      baseCurrencyCode: this.baseCurrencyCode.trim().toUpperCase()
    }).subscribe({
      next: () => {
        this.groupCode = '';
        this.groupName = '';
        this.loadAll();
      },
      error: () => this.messages.add({ severity: 'error', summary: 'Create group failed' })
    });
  }

  createRun(): void {
    const fiscalYear = Number(this.fiscalYearText);
    const periodNumber = Number(this.periodNumberText);
    const eliminationAmount = Number(this.eliminationAmountText);
    if (!this.runGroupId.trim() || Number.isNaN(fiscalYear) || Number.isNaN(periodNumber) || Number.isNaN(eliminationAmount)) return;

    this.api.createConsolidationRun({
      groupId: this.runGroupId.trim(),
      fiscalYear,
      periodNumber,
      eliminationAmount,
      currencyCode: this.baseCurrencyCode.trim().toUpperCase()
    }).subscribe({
      next: () => this.loadAll(),
      error: () => this.messages.add({ severity: 'error', summary: 'Create run failed' })
    });
  }

  loadEntries(runId: string): void {
    this.api.getConsolidationEntries(runId).subscribe({ next: rows => this.entries.set(rows) });
  }
}
